import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';

const CHAT_KEY = 'pointspilot_advisor_chats';

function loadChats() {
  try { return JSON.parse(localStorage.getItem(CHAT_KEY)) || []; } catch { return []; }
}
function saveChats(chats) {
  try { localStorage.setItem(CHAT_KEY, JSON.stringify(chats)); } catch {}
}

export default function ChatBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadChats());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0) saveChats(messages);
  }, [messages]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setPulse(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg, ts: Date.now() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const { content } = await api.sendAdvisorMessage(
        newMessages.map(m => ({ role: m.role, content: m.content }))
      );
      setMessages(prev => [...prev, { role: 'assistant', content, ts: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Having trouble connecting. Check the backend.", ts: Date.now() }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const clearHistory = () => {
    setMessages([]);
    saveChats([]);
  };

  const quickQuestions = [
    "What's the best value destination?",
    "How should I split my points?",
    "What's happening in September?",
  ];

  return (
    <>
      {/* ═══ MINI CHAT WINDOW ═══ */}
      {open && (
        <div className="fixed bottom-[130px] right-3 left-3 sm:left-auto sm:right-6 sm:bottom-24 z-[45] sm:w-[360px] max-h-[60vh] sm:max-h-[500px] bg-navy-light/98 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden animate-scale-pop">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-brand-ink/10 bg-gradient-to-r from-teal/10 to-navy-light">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-teal/20 flex items-center justify-center text-teal font-bold">AI</div>
              <div>
                <div className="text-sm font-bold text-white">Points Advisor</div>
                <div className="text-[10px] text-teal/70">Ask anything about your trip</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Link
                to="/advisor"
                onClick={() => setOpen(false)}
                className="px-2 py-1 text-[10px] text-brand-ink/55 hover:text-brand-ink/70 transition"
                title="Open full chat"
              >
                expand
              </Link>
              {messages.length > 0 && (
                <button onClick={clearHistory} className="px-2 py-1 text-[10px] text-brand-ink/45 hover:text-brand-ink/65 transition" title="Clear chat">
                  clear
                </button>
              )}
              <button onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center text-brand-ink/55 hover:text-brand-ink/75 transition text-lg rounded-full hover:bg-brand-ink/5">
                x
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-[200px] max-h-[340px]">
            {messages.length === 0 && (
              <div className="space-y-3 pt-2">
                <div className="text-center text-brand-ink/55 text-xs">Ask anything about points, transfers, or destinations.</div>
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); sendMessage(q); }}
                    className="w-full text-left px-3 py-2 bg-brand-ink/5 border border-brand-ink/10 rounded-lg text-xs text-brand-ink/70 hover:text-brand-ink/80 hover:bg-white/[0.06] transition"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-teal/20 text-white'
                    : 'bg-white/[0.06] border border-brand-ink/10 text-brand-ink/85'
                }`}>
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/[0.06] border border-brand-ink/10 rounded-xl px-3 py-2.5 flex items-center gap-1">
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-teal/70" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-teal/70" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-teal/70" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-3 pb-3 pt-1 border-t border-brand-ink/10">
            <div className="flex gap-1.5">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask anything..."
                className="flex-1 bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2 text-xs text-white placeholder-white/25 focus:outline-none focus:border-teal"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="px-3 py-2 bg-teal text-navy font-bold rounded-lg text-xs hover:bg-teal-dark transition disabled:opacity-40"
              >
                Go
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ═══ FLOATING BUBBLE ═══ */}
      <button
        onClick={() => setOpen(!open)}
        className={`chat-bubble-floating fixed bottom-[70px] right-4 z-[46] group transition-all duration-300 ${open ? 'scale-90' : 'hover:scale-105'}`}
      >
        <div className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-teal to-teal-dark shadow-xl shadow-teal/20 flex items-center justify-center transition-all ${pulse ? 'animate-pulse-glow' : ''} ${open ? 'ring-2 ring-teal/50' : ''}`}>
          <span className="text-navy font-extrabold text-lg">AI</span>

          {/* Unread indicator */}
          {!open && messages.length > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gold rounded-full flex items-center justify-center text-[9px] font-bold text-navy shadow-lg">
              {messages.filter(m => m.role === 'assistant').length}
            </div>
          )}
        </div>

        {/* Tooltip */}
        {!open && (
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-navy-light border border-brand-ink/10 rounded-lg text-[10px] text-brand-ink/75 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            Ask the advisor
          </div>
        )}
      </button>
    </>
  );
}
